import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    Chip,
    TextField,
    MenuItem,
    Stack,
    IconButton,
    CircularProgress,
    TableContainer
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AppHeader from "../components/AppHeader";
import { formatDDMMYYYY, formatYangon } from "../services/datetimeService";
import dayjs from "dayjs";

export default function ManualRequestsReport({ setIsLoggedIn }) {
    const today = dayjs().format("YYYY-MM-DD");
    const sevenDaysAgo = dayjs().subtract(7, "days").format("YYYY-MM-DD");

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        date_from: sevenDaysAgo,
        date_to: today,
        status: "All",
        staff_code: ""
    });

    const tenant = localStorage.getItem("tenant") || "";

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL1}/approval/manual/report`,
                {
                    params: filters,
                    withCredentials: true,
                    headers: { "X-Tenant": tenant }
                }
            );

            if (res.data.success) {
                setItems(res.data.data);
            }
        } catch (err) {
            console.error("Report load error:", err);
        }
        setLoading(false);
    }, [tenant, filters]);

    useEffect(() => {
        loadData();
    }, []);

    const handleSearch = () => {
        loadData();
    };

    const handleExport = async () => {
        try {
            const queryParams = new URLSearchParams(filters).toString();
            const url = `${process.env.REACT_APP_API_URL1}/approval/manual/report/export?${queryParams}`;

            const response = await fetch(url, {
                credentials: "include",
                headers: { "X-Tenant": tenant }
            });

            if (!response.ok) throw new Error("Export failed");

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = downloadUrl;
            a.download = `Manual_Requests_${filters.date_from}_to_${filters.date_to}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error("Export error:", err);
            alert("Failed to export report");
        }
    };

    return (
        <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", bgcolor: "#f1f8e9", overflow: "hidden" }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />

            <Box sx={{ flexGrow: 1, p: 2, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: "#1b5e20", lineHeight: 1 }}>
                        Manual Requests Report
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <IconButton size="small" onClick={loadData} sx={{ bgcolor: "white", border: "1px solid #ddd" }}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                        <Button variant="contained" size="small" onClick={handleExport} sx={{ bgcolor: "#2e7d32", borderRadius: "8px" }}>
                            Export Excel
                        </Button>
                    </Stack>
                </Box>

                <Paper sx={{ p: 1.5, mb: 1.5, bgcolor: "#ffffff", borderRadius: "12px" }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <TextField
                            label="From" type="date" size="small" InputLabelProps={{ shrink: true }}
                            value={filters.date_from}
                            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                            sx={{ width: 140 }}
                        />
                        <TextField
                            label="To" type="date" size="small" InputLabelProps={{ shrink: true }}
                            value={filters.date_to}
                            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                            sx={{ width: 140 }}
                        />
                        <TextField
                            select label="Status" size="small" value={filters.status}
                            sx={{ minWidth: 120 }}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        >
                            <MenuItem value="All">All Statuses</MenuItem>
                            <MenuItem value="draft">Draft</MenuItem>
                            <MenuItem value="submitted">Submitted</MenuItem>
                            <MenuItem value="in_review">In Review</MenuItem>
                            <MenuItem value="approved">Approved</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                            <MenuItem value="cancelled">Cancelled</MenuItem>
                        </TextField>
                        <TextField
                            label="Staff Code" size="small" placeholder="EMP001"
                            value={filters.staff_code}
                            onChange={(e) => setFilters({ ...filters, staff_code: e.target.value })}
                        />
                        <Button variant="contained" size="small" onClick={handleSearch} sx={{ height: 40, bgcolor: "#1b5e20", px: 3, borderRadius: "8px" }}>
                            Search
                        </Button>
                    </Stack>
                </Paper>

                <TableContainer component={Paper} sx={{ flexGrow: 1, borderRadius: "12px", border: "1px solid #eee", overflow: "auto" }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 800, bgcolor: "#f8faf8", fontSize: "0.75rem" }}>DATE</TableCell>
                                <TableCell sx={{ fontWeight: 800, bgcolor: "#f8faf8", fontSize: "0.75rem" }}>CODE</TableCell>
                                <TableCell sx={{ fontWeight: 800, bgcolor: "#f8faf8", fontSize: "0.75rem" }}>NAME</TableCell>
                                <TableCell sx={{ fontWeight: 800, bgcolor: "#f8faf8", fontSize: "0.75rem" }}>DEPT</TableCell>
                                <TableCell sx={{ fontWeight: 800, bgcolor: "#f8faf8", fontSize: "0.75rem" }}>STATUS</TableCell>
                                <TableCell sx={{ fontWeight: 800, bgcolor: "#f8faf8", fontSize: "0.75rem" }}>REASON</TableCell>
                                <TableCell sx={{ fontWeight: 800, bgcolor: "#f8faf8", fontSize: "0.75rem" }}>REQ IN</TableCell>
                                <TableCell sx={{ fontWeight: 800, bgcolor: "#f8faf8", fontSize: "0.75rem" }}>REQ OUT</TableCell>
                                <TableCell sx={{ fontWeight: 800, bgcolor: "#f8faf8", fontSize: "0.75rem" }}>SUBMITTED</TableCell>
                                <TableCell sx={{ fontWeight: 800, bgcolor: "#f8faf8", fontSize: "0.75rem" }}>REVIEWED</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 10 }}>
                                        <CircularProgress size={30} color="success" />
                                    </TableCell>
                                </TableRow>
                            ) : items.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.secondary">No requests found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                items.map((row) => (
                                    <TableRow key={row.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell sx={{ fontSize: "0.75rem" }}>{formatDDMMYYYY(row.request_date)}</TableCell>
                                        <TableCell sx={{ fontSize: "0.75rem", fontWeight: 700 }}>{row.staff_code}</TableCell>
                                        <TableCell sx={{ fontSize: "0.75rem" }}>{row.staff_name}</TableCell>
                                        <TableCell sx={{ fontSize: "0.75rem" }}>{row.department || "-"}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={row.status.toUpperCase()}
                                                size="small"
                                                color={
                                                    row.status === "approved" ? "success" :
                                                        (row.status === "rejected" || row.status === "cancelled" ? "error" : "warning")
                                                }
                                                sx={{ fontSize: "0.6rem", fontWeight: 800, height: 18, borderRadius: "4px" }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontSize: "0.75rem", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {row.reason || "-"}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: "0.75rem" }}>{row.requested_clock_in_time ? formatYangon(row.requested_clock_in_time) : "-"}</TableCell>
                                        <TableCell sx={{ fontSize: "0.75rem" }}>{row.requested_clock_out_time ? formatYangon(row.requested_clock_out_time) : "-"}</TableCell>
                                        <TableCell sx={{ fontSize: "0.75rem" }}>{formatYangon(row.submitted_at)}</TableCell>
                                        <TableCell sx={{ fontSize: "0.75rem" }}>
                                            {row.status === "approved" || row.status === "rejected" ? formatYangon(row.reviewed_at) : "-"}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
}
